import { memo, useCallback, useMemo, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Badge, Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useGetUser } from "../../api/queries/useGetUser";
import { EXPLORER_SEGMENT_OPTIONS } from "../../explorer/explorerSegments";
import {
  getEventObjectiveDisplayLabel,
  getEventStageDisplayLabel,
} from "../../i18n/eventRegistrationI18n";
import type { User } from "../../types/Types";
import { getAvatarUrl, getInitials } from "../../utils/connectionUserUtils";
import {
  isPaidPortalMember,
  PROFILE_MEMBERSHIP_FIELD_KEYS,
} from "../../utils/membershipTier";
import { ExpandableText } from "../Account/AccountProfileLayout";
import DetailPageHeader from "../common/DetailPageHeader";

const CARD_BODY_MIN_HEIGHT = 320;
const AVATAR_SIZE_STYLE = {
  width: 88,
  height: 88,
  objectFit: "cover" as const,
};

const EXCLUDED_KEYS = [
  "password",
  "objectId",
  "isGDPRConsent",
  "isEmailUpdates",
  "isTermConditions",
  "isDirectory",
  "hasPaid",
  "hasCompletedProfile",
  "id",
  "isAdmin",
  "membershipId",
  "consentToShareProfile",
  "BannerImage",
  "explorerLimitsJson",
  "firstName",
  "lastName",
];

const IMAGE_KEYS = new Set([
  "IdDocument",
  "BusinessLogo",
  "PersonalProfessionalPhoto",
  "ProfessionalPhoto",
]);

const DATE_TIME_KEYS = new Set([
  "createdDate",
  "lastLoggedIn",
  "discountEndDate",
  "earlyStageVerificationSubmittedAt",
  "earlyStageVerifiedAt",
]);

const BOOLEAN_KEYS = new Set(["isPortalUser", "isAnnualMembership"]);

const SECTION_CONFIG: { id: string; title: string; keys: string[] }[] = [
  {
    id: "contact",
    title: "Contact & location",
    keys: [
      "email",
      "phoneNumber",
      "city",
      "country",
      "county",
      "gender",
      "personalGender",
      "languagePreference",
      "personalLanguagePreference",
      "dateOfBirth",
    ],
  },
  {
    id: "business",
    title: "Business",
    keys: [
      "businessName",
      "businessSectorIndustry",
      "personalPositionCompany",
      "businessWebsiteUrl",
      "businessCountryOperation",
      "businessAbout",
      "businessAddressLineOne",
      "businessAddressLineTwo",
      "businessCountry",
      "businessPostCode",
      "businessTelephoneNumber",
      "businessCurrency",
      "businessTurnover",
      "businessObjectives",
      "businessProductServices",
      "businessDiscount",
      "businessDiscountCode",
    ],
  },
  {
    id: "education",
    title: "Education & interests",
    keys: [
      "universityCollege",
      "educationLevel",
      "studyInterest",
      "skillsExperience",
      "mentorshipObjectives",
      "mentorshipExpertise",
      "mentorshipFormat",
      "platformInterest",
      "peerNetworking",
      "productInterested",
      "aboutYou",
      "personalAbout",
    ],
  },
  {
    id: "explorer",
    title: "Explorer profile",
    keys: [
      "userCategory",
      "explorerAttendeeType",
      "objectivesForAttending",
      "stageOfBusiness",
      "sectorsOfInterest",
      "eventObjectives",
    ],
  },
  {
    id: "membership",
    title: "Membership",
    keys: [
      "membershipName",
      "isAnnualMembership",
      "discountCode",
      "discountEndDate",
    ],
  },
  {
    id: "billing",
    title: "Billing & access",
    keys: ["subscriptionId", "stripeStatus", "isPortalUser"],
  },
  {
    id: "earlyStage",
    title: "Early Stage verification",
    keys: [
      "earlyStageVerificationStatus",
      "earlyStageAgeTier",
      "earlyStagePriceTier",
      "earlyStageVerificationSubmittedAt",
      "earlyStageVerifiedAt",
      "earlyStageResubmissionCount",
    ],
  },
  {
    id: "account",
    title: "Account activity",
    keys: ["createdDate", "lastLoggedIn", "referralCode", "referralCodeUsed"],
  },
];

const LABEL_OVERRIDES: Record<string, string> = {
  isAnnualMembership: "Billing cycle",
  businessAddressLineOne: "Address line 1",
  businessAddressLineTwo: "Address line 2",
  businessName: "Business name",
  businessSectorIndustry: "Sector / industry",
  personalPositionCompany: "Job title",
  phoneNumber: "Phone number",
  userCategory: "User category",
  explorerAttendeeType: "Explorer segment",
  objectivesForAttending: "Objectives for attending",
  stageOfBusiness: "Stage of business",
  sectorsOfInterest: "Sectors of interest",
  createdDate: "Created",
  lastLoggedIn: "Last logged in",
  referralCode: "Referral code",
  subscriptionId: "Subscription ID",
  stripeStatus: "Stripe status",
  isPortalUser: "Portal user",
  earlyStageVerificationStatus: "Verification status",
  earlyStageAgeTier: "Age tier",
  earlyStagePriceTier: "Price tier",
  earlyStageVerificationSubmittedAt: "Submitted",
  earlyStageVerifiedAt: "Verified",
  earlyStageResubmissionCount: "Resubmissions",
  IdDocument: "ID document",
  BusinessLogo: "Business logo",
  PersonalProfessionalPhoto: "Professional photo",
  ProfessionalPhoto: "Professional photo",
};

function formatFieldLabel(key: string): string {
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
  return key
    .replace(/^(business|personal|earlyStage)/i, (m) =>
      m.toLowerCase() === "earlystage" ? "Early stage " : "",
    )
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

function humanizeToken(raw: string): string {
  return raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatDateValue(value: string | number, withTime = false): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return withTime
    ? date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function isTruthyBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

function getImageAlt(key: string): string {
  if (key === "IdDocument") return "ID document";
  if (key === "BusinessLogo") return "Business logo";
  if (key === "PersonalProfessionalPhoto" || key === "ProfessionalPhoto") {
    return "Professional photo";
  }
  return "Profile image";
}

function statusBadge(
  label: string,
  tone: "success" | "warning" | "danger" | "neutral",
) {
  if (tone === "success") {
    return (
      <Badge bg="success-subtle" text="success-emphasis" pill className="fw-semibold">
        {label}
      </Badge>
    );
  }
  if (tone === "warning") {
    return (
      <Badge bg="warning-subtle" text="warning-emphasis" pill className="fw-semibold">
        {label}
      </Badge>
    );
  }
  if (tone === "danger") {
    return (
      <Badge bg="danger-subtle" text="danger-emphasis" pill className="fw-semibold">
        {label}
      </Badge>
    );
  }
  return (
    <Badge bg="secondary-subtle" text="secondary-emphasis" pill className="fw-semibold">
      {label}
    </Badge>
  );
}

function formatFieldValue(
  key: string,
  value: unknown,
  t: (key: string) => string,
): ReactNode {
  if (value == null || value === "") return "—";

  if (IMAGE_KEYS.has(key) && value) {
    return (
      <div className="ratio ratio-1x1 rounded-3 border overflow-hidden bg-light">
        <img
          src={String(value)}
          alt={getImageAlt(key)}
          className="w-100 h-100 object-fit-cover"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }

  if (key === "isAnnualMembership") {
    return value === 0 || value === "0" ? "Monthly" : "Annual";
  }

  if (BOOLEAN_KEYS.has(key) || typeof value === "boolean") {
    return isTruthyBoolean(value) ? "Yes" : "No";
  }

  if (key === "dateOfBirth") {
    return formatDateValue(value as string | number, false);
  }

  if (DATE_TIME_KEYS.has(key) || /At$/.test(key) || /Date$/i.test(key)) {
    const raw = String(value);
    if (/\d{4}-\d{2}-\d{2}/.test(raw)) {
      return formatDateValue(value as string | number, true);
    }
  }

  if (key === "stripeStatus") {
    const raw = humanizeToken(String(value));
    const lower = String(value).toLowerCase();
    const tone =
      lower === "active"
        ? "success"
        : lower === "canceled" ||
            lower === "cancelled" ||
            lower === "incomplete_expired" ||
            lower === "unpaid"
          ? "danger"
          : "warning";
    return statusBadge(raw, tone);
  }

  if (key === "earlyStageVerificationStatus") {
    const raw = humanizeToken(String(value));
    const lower = String(value).toLowerCase();
    const tone =
      lower === "approved"
        ? "success"
        : lower === "rejected" || lower === "denied"
          ? "danger"
          : "warning";
    return statusBadge(raw, tone);
  }

  if (key === "explorerAttendeeType") {
    const raw = String(value).trim();
    const match = EXPLORER_SEGMENT_OPTIONS.find((o) => o.value === raw);
    return match?.label || humanizeToken(raw);
  }

  if (key === "objectivesForAttending" || key === "eventObjectives") {
    const raw = String(value).trim();
    const labeled = getEventObjectiveDisplayLabel(raw, t);
    if (labeled && labeled !== raw) return labeled;
    return humanizeToken(raw);
  }

  if (key === "stageOfBusiness") {
    const raw = String(value).trim();
    const labeled = getEventStageDisplayLabel(raw, t);
    return labeled || humanizeToken(raw);
  }

  if (key === "earlyStageAgeTier" || key === "earlyStagePriceTier") {
    return humanizeToken(String(value));
  }

  const text = String(value);
  if (text.includes("_") && !text.includes("@") && !text.includes("://")) {
    return humanizeToken(text);
  }
  return text;
}

type ProfileField = { key: string; value: unknown };

function UserProfileInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: objectId } = useParams();
  const { data, isLoading } = useGetUser({
    id: objectId || "",
    enabled: !!objectId,
  });
  const userData = { ...data?.user, ...data?.imageUrls } as Partial<User> &
    Record<string, unknown>;
  const showMembershipFields = useMemo(
    () => isPaidPortalMember(userData as User),
    [userData],
  );

  const handleBack = useCallback(() => {
    const state = location.state as {
      from?: string;
      adminTab?: string;
      usersSubTab?: "all" | "members" | "explorer";
    } | null;
    if (state?.from) {
      navigate(state.from, {
        state: {
          adminTab: state.adminTab,
          usersSubTab: state.usersSubTab,
        },
      });
      return;
    }
    navigate(-1);
  }, [location.state, navigate]);

  const fields = useMemo((): ProfileField[] => {
    return Object.entries(userData || {})
      .filter(([key, value]) => {
        if (key.trim() === "" || EXCLUDED_KEYS.includes(key)) return false;
        if (!showMembershipFields && PROFILE_MEMBERSHIP_FIELD_KEYS.has(key)) {
          return false;
        }
        if (BOOLEAN_KEYS.has(key) || typeof value === "boolean") {
          return value !== null && value !== undefined && value !== "";
        }
        if (value === 0) return true;
        return value !== null && value !== "";
      })
      .map(([key, value]) => ({ key, value }));
  }, [showMembershipFields, userData]);

  const fieldsByKey = useMemo(() => {
    const map = new Map<string, unknown>();
    fields.forEach(({ key, value }) => map.set(key, value));
    return map;
  }, [fields]);

  const sections = useMemo(() => {
    const used = new Set<string>();
    const result = SECTION_CONFIG.map((section) => {
      const items = section.keys
        .filter((key) => {
          if (!fieldsByKey.has(key)) return false;
          const value = fieldsByKey.get(key);
          if (value === null || value === undefined || value === "") return false;
          return true;
        })
        .map((key) => {
          used.add(key);
          return { key, value: fieldsByKey.get(key) };
        });
      return { ...section, items };
    }).filter((section) => section.items.length > 0);

    const leftover = fields.filter(
      ({ key }) => !used.has(key) && !IMAGE_KEYS.has(key),
    );
    if (leftover.length > 0) {
      result.push({
        id: "other",
        title: "Other details",
        keys: leftover.map((f) => f.key),
        items: leftover,
      });
    }

    const media = Array.from(IMAGE_KEYS)
      .filter((key) => fieldsByKey.has(key) && fieldsByKey.get(key))
      .map((key) => ({ key, value: fieldsByKey.get(key) }));
    if (media.length > 0) {
      result.push({
        id: "media",
        title: "Photos & documents",
        keys: media.map((f) => f.key),
        items: media,
      });
    }

    return result;
  }, [fields, fieldsByKey]);

  const fullName = useMemo(() => {
    const first = String(userData.firstName || "").trim();
    const last = String(userData.lastName || "").trim();
    return `${first} ${last}`.trim() || "User profile";
  }, [userData.firstName, userData.lastName]);

  const avatarUrl = getAvatarUrl(userData as User);
  const initials = getInitials(userData as User);
  const jobTitle = String(userData.personalPositionCompany || "").trim();
  const businessName = String(userData.businessName || "").trim();
  const subtitle = [jobTitle, businessName].filter(Boolean).join(" · ");
  const category = String(userData.userCategory || "").trim();
  const membershipName = String(userData.membershipName || "").trim();
  const locationLabel = [userData.city, userData.country]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(", ");

  return (
    <div className="container-fluid px-0 pb-4">
      <DetailPageHeader
        onBack={handleBack}
        eyebrow={t("profile.defaultTitle")}
        title={fullName || t("profile.defaultTitle")}
      />
      <div className="card shadow-lg border-0 overflow-hidden">
        <div
          className="card-body p-0 bg-light"
          style={{ minHeight: CARD_BODY_MIN_HEIGHT }}
        >
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" role="status" />
            </div>
          ) : (
            <>
              <div className="bg-white border-bottom px-3 px-md-4 py-4">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={fullName}
                      className="rounded-circle border border-3 border-white shadow-sm flex-shrink-0"
                      width={88}
                      height={88}
                      style={AVATAR_SIZE_STYLE}
                    />
                  ) : (
                    <div
                      className="rounded-circle border border-3 border-white shadow-sm flex-shrink-0 d-flex align-items-center justify-content-center text-white fw-bold bg-project-gradient"
                      style={AVATAR_SIZE_STYLE}
                      aria-hidden
                    >
                      {initials}
                    </div>
                  )}
                  <div className="min-w-0 flex-grow-1">
                    <h2 className="h4 mb-1 text-truncate fw-bold">
                      {fullName}
                    </h2>
                    {subtitle ? (
                      <p className="mb-2 text-muted small">{subtitle}</p>
                    ) : null}
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {category ? (
                        <Badge bg="primary" pill className="fw-semibold">
                          {humanizeToken(category)}
                        </Badge>
                      ) : null}
                      {membershipName ? (
                        <Badge
                          bg="light"
                          text="dark"
                          pill
                          className="fw-semibold border"
                        >
                          {membershipName}
                        </Badge>
                      ) : null}
                      {locationLabel ? (
                        <span className="text-muted small">{locationLabel}</span>
                      ) : null}
                    </div>
                    {userData.email ? (
                      <p className="mb-0 mt-2 small text-break text-muted">
                        {String(userData.email)}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="px-3 px-md-4 py-4">
                {sections.length === 0 ? (
                  <p className="text-muted mb-0">No profile details available.</p>
                ) : (
                  <Row className="g-3">
                    {sections.map((section) => (
                      <Col
                        key={section.id}
                        xs={12}
                        lg={section.id === "media" ? 12 : 6}
                      >
                        <section className="card h-100 border shadow-sm rounded-3 overflow-hidden">
                          <div className="card-header bg-light border-bottom py-3">
                            <h3 className="h6 mb-0 fw-bold">
                              {section.title}
                            </h3>
                          </div>
                          {section.id === "media" ? (
                            <div className="card-body">
                              <Row className="g-3">
                                {section.items.map(({ key, value }) => (
                                  <Col key={key} xs={12} sm={6} md={4} lg={3}>
                                    <figure className="mb-0">
                                      {formatFieldValue(key, value, t)}
                                      <figcaption className="small fw-semibold text-muted mt-2">
                                        {formatFieldLabel(key)}
                                      </figcaption>
                                    </figure>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          ) : (
                            <Row className="g-0">
                              {section.items.map(({ key, value }) => (
                                <Col
                                  key={key}
                                  xs={12}
                                  md={6}
                                  className="border-bottom p-3 min-w-0"
                                >
                                  <div className="small text-muted fw-semibold mb-1 text-truncate">
                                    {formatFieldLabel(key)}
                                  </div>
                                  <ExpandableText className="fw-semibold">
                                    {formatFieldValue(key, value, t)}
                                  </ExpandableText>
                                </Col>
                              ))}
                            </Row>
                          )}
                        </section>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

UserProfileInner.displayName = "UserProfile";

export default memo(UserProfileInner);
